<template>
  <div class="flex justify-center gap-8">
    <!-- Save Button -->
    <s-button
      v-if="!isTestTab"
      variant="primary"
      size="lg"
      :disabled="disabled"
      class="!min-w-160"
      @click="onSaveClick"
    >
      {{ $t('studio.prj_prod.execution_save_btn') }}
    </s-button>

    <s-tooltip
      v-else
      theme="studio-button-tooltip"
      arrow
      :duration="0"
      useFlip
      flipOnUpdate
      placement="top"
      trigger="mouseenter focus"
      :zIndex="2500"
    >
      <template #default>
        <span style="text-align: center;">
          <safe-html :html="$t('studio.execution_setting.test_package_added.save_tooltip')" />
        </span>
      </template>
      <template #target>
        <s-button variant="secondary" size="lg" :disabled="disabled" class="w-160 h-56 mt-8" @click="onSaveClick">
          {{ $t('studio.prj_prod.execution_save_btn') }}
        </s-button>
      </template>
    </s-tooltip>

    <!-- Live Button -->
    <STooltip
      theme="studio-button-tooltip"
      arrow
      :duration="0"
      useFlip
      flipOnUpdate
      placement="top"
      trigger="mouseenter focus"
      :zIndex="600"
    >
      <template #default>
        <span style="text-align: center;">
          <safe-html :html="$t('studio.execution_setting.test_package_added.live_apply_tooltip')" />
        </span>
      </template>
      <template #target>
        <s-button
          v-if="isTestTab && (verifyStatus === VERIFY_STATUS.PASS || planStatus === PLAN_STATUS.RELEASE)"
          variant="primary"
          size="lg"
          :disabled="disabled"
          class="w-160 h-56 mt-8"
          @click="handleLiveApply"
        >
          {{ $t('studio.common.live_apply_btn') }}
        </s-button>
      </template>
    </STooltip>
  </div>
</template>
<script setup lang="ts">
import { defineEmits, defineProps } from 'vue';

import TestOkConfirmDialog from '@/components/app/dialog/test-ok-confirm-dialog.vue';
import SafeHtml from '@/components/common/safe-html.vue';
import { showDialog } from '@/composables/useDialog';
import { PLAN_STATUS, VERIFY_STATUS } from '@/constants/products.const';

interface LaunchSettingSaveButtonProps {
  isLived: boolean;
  onSubmit: (isApplyLive: boolean) => Promise<boolean | any>;
  disabled?: boolean;
  isTestTab?: boolean;
  verifyStatus?: string;
  planStatus?: string;
  onResetClearRunOptionsApi?: () => void;
}

const props = defineProps<LaunchSettingSaveButtonProps>();

const emit = defineEmits<{
  onLiveApply: [];
}>();

const onSaveClick = async () => {
  const result = await props.onSubmit(false);

  // Reset isCallClearRunOptionsApi to false after save
  if (result !== false && props.onResetClearRunOptionsApi) {
    props.onResetClearRunOptionsApi();
  }
};

const handleLiveApply = async () => {
  if (props.isTestTab) {
    const saveResult = await props.onSubmit(true);

    // Reset isCallClearRunOptionsApi to false after live apply
    if (saveResult !== false && props.onResetClearRunOptionsApi) {
      props.onResetClearRunOptionsApi();
    }

    if (saveResult !== false) {
      // Open dialog to confirm live apply
      const confirmed = await showDialog({
        component: TestOkConfirmDialog,
        props: {
          class: 'max-w-[31.2rem]'
        },
        severity: 'info'
      });

      if (confirmed) {
        emit('onLiveApply');
      }
    }
  }
};
</script>
